import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, MessageCircle, Calendar, Heart, Gauge, Fuel, Settings2, User, MapPin, ShieldCheck, Check, Share2, Calculator, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, resolveContact } from "@/lib/api";
import { formatPriceLakh, formatPriceFull, formatKm, vehicleTitle, buildWhatsAppLink } from "@/lib/format";
import { useShortlist } from "@/context/ShortlistContext";
import { VehicleCard } from "@/components/vehicle/VehicleCard";
import { TestDriveDialog } from "@/components/vehicle/TestDriveDialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { MobileStickyBar } from "@/components/layout/MobileStickyBar";
import { PhotoLightbox, useSwipe } from "@/components/vehicle/PhotoLightbox";

export default function VehicleDetailPage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [settings, setSettings] = useState({});
  const [tdOpen, setTdOpen] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const { has, toggle } = useShortlist();

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/vehicles/${id}`).then((r) => {
      setV(r.data);
      api.get(`/vehicles?body_type=${encodeURIComponent(r.data.body_type)}&limit=6`).then((rr) =>
        setSimilar(rr.data.items.filter((x) => x.id !== r.data.id).slice(0, 4))
      );
    }).catch(() => setV(false));
    api.get("/settings").then((r) => setSettings(r.data));
  }, [id]);

  const swipe = useSwipe(() => setImgIdx((i) => (v?.images?.length > 1 ? (i + 1) % v.images.length : i)), () => setImgIdx((i) => (v?.images?.length > 1 ? (i - 1 + v.images.length) % v.images.length : i)));

  if (v === null) return <div className="cs-container py-20 text-center text-gray-500">Loading…</div>;
  if (v === false) return <div className="cs-container py-20 text-center"><p>Vehicle not found</p><Link to="/buy" className="btn-primary mt-4">Back to Buy Cars</Link></div>;

  const imgRaw = v.images?.[imgIdx] || v.images?.[0] || "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1200";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const toAbs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);
  const img = toAbs(imgRaw);
  const waMsg = `Hi Carz Swag, I'm interested in the ${vehicleTitle(v)} (Stock: ${v.stock_id}). Please share more details.`;
  const { phone, whatsapp, phoneIsDemo, whatsappIsDemo } = resolveContact(settings);
  const isSold = v.status === "sold";
  const imgCount = v.images?.length || 0;
  const step = (d) => imgCount > 1 && setImgIdx((i) => (i + d + imgCount) % imgCount);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareMsg = `Check out this ${vehicleTitle(v)} at Carz Swag — ${formatPriceLakh(v.price)}. ${shareUrl}`;
  const shareOnWA = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
    window.open(url, "_blank", "noopener");
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: vehicleTitle(v), text: shareMsg, url: shareUrl }); return; } catch (_) {}
    }
    shareOnWA();
  };

  return (
    <div data-testid="vehicle-detail-page" className="overflow-x-hidden">
      <div className="cs-container py-6 text-xs text-gray-500">
        <Link to="/buy" className="hover:text-[#940C2C]">Buy Cars</Link> / <span className="text-gray-800">{v.make} {v.model}</span>
      </div>

      <div className="cs-container pb-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-8">
        <div className="min-w-0">
          <div className="relative w-full rounded-2xl overflow-hidden bg-gray-100 aspect-[16/10] max-h-[70vh] lg:max-h-[560px]" data-testid="vehicle-gallery-main" {...swipe}>
            <img src={img} alt={vehicleTitle(v)} onClick={() => imgCount > 0 && setLightbox(true)} className={`w-full h-full object-contain ${imgCount > 0 ? "cursor-zoom-in" : ""} ${isSold ? "grayscale" : ""}`} data-testid="vehicle-main-image" />
            {isSold && <div className="absolute top-4 left-4 bg-[#777] text-white font-bold px-3 py-1.5 rounded-md text-xs uppercase tracking-wider">Sold</div>}
            {v.cs_certified && <div className="absolute top-4 right-4 badge-red font-bold px-3 py-1.5 rounded-md text-xs uppercase tracking-wider inline-flex items-center gap-1"><ShieldCheck size={12} /> CS Certified</div>}
            {imgCount > 1 && (
              <>
                <button onClick={() => step(-1)} aria-label="Previous photo" className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/90 text-gray-900 shadow hover:bg-white transition-colors" data-testid="gallery-prev-btn"><ChevronLeft size={20} /></button>
                <button onClick={() => step(1)} aria-label="Next photo" className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center rounded-full bg-white/90 text-gray-900 shadow hover:bg-white transition-colors" data-testid="gallery-next-btn"><ChevronRight size={20} /></button>
                <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-md" data-testid="gallery-counter">{imgIdx + 1} / {imgCount}</div>
              </>
            )}
            <button onClick={nativeShare} className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 bg-white/95 text-gray-900 text-xs font-bold px-3 py-2 rounded-md shadow hover:bg-white" data-testid="share-vehicle-btn">
              <Share2 size={13} /> Share
            </button>
          </div>
          {v.images?.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar max-w-full" data-testid="vehicle-gallery-thumbs">
              {v.images.map((im, i) => (
                <button key={i} ref={i === imgIdx ? centerThumb : undefined} onClick={() => setImgIdx(i)} data-testid={`vehicle-thumb-${i}`} className={`shrink-0 w-20 sm:w-24 aspect-[4/3] rounded-md overflow-hidden border-2 ${i === imgIdx ? "border-[#940C2C]" : "border-transparent"}`}>
                  <img src={toAbs(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8 space-y-8">
            <Section title="Overview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={<Calendar size={14} />} label="Year" value={v.year} />
                <Stat icon={<Gauge size={14} />} label="KM Driven" value={formatKm(v.km_driven)} />
                <Stat icon={<Fuel size={14} />} label="Fuel" value={v.fuel} />
                <Stat icon={<Settings2 size={14} />} label="Transmission" value={v.transmission} />
                <Stat icon={<User size={14} />} label="Owners" value={v.owners} />
                <Stat label="Body Type" value={v.body_type} />
                <Stat label="Colour" value={v.color || "—"} />
                <Stat label="Stock ID" value={v.stock_id} />
              </div>
            </Section>

            <Section title="EMI Calculator">
              <EMICalculator carPrice={v.price} />
            </Section>

            {(v.engine || v.mileage || v.registration_state || v.insurance) && (
              <Section title="Specifications">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-3 gap-x-6 text-sm">
                  {v.engine && <Row label="Engine" value={v.engine} />}
                  {v.mileage && <Row label="Mileage" value={v.mileage} />}
                  {v.registration_state && <Row label="Registration" value={v.registration_state} />}
                  {v.registration_number && <Row label="Reg. Number" value={v.registration_number} />}
                  {v.insurance && <Row label="Insurance" value={v.insurance} />}
                  {v.insurance_valid_till && <Row label="Insurance Till" value={v.insurance_valid_till} />}
                </div>
              </Section>
            )}

            {v.features?.length > 0 && (
              <Section title="Features & Equipment">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {v.features.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-gray-700"><Check size={14} className="text-[#16A34A]" /> {f}</div>
                  ))}
                </div>
              </Section>
            )}

            {v.cs_certified && v.inspection && Object.keys(v.inspection).length > 0 && (
              <Section title="CS Certified Inspection">
                <Accordion type="multiple" className="w-full">
                  {Object.entries(v.inspection).map(([cat, note]) => (
                    <AccordionItem key={cat} value={cat}>
                      <AccordionTrigger className="font-semibold">{cat}</AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600">{note || "Verified by Carz Swag team."}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Section>
            )}

            {v.service_history && <Section title="Service History"><p className="text-sm text-gray-700 whitespace-pre-wrap">{v.service_history}</p></Section>}
            {v.description && <Section title="Vehicle Description"><p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{v.description}</p></Section>}
          </div>
        </div>

        <aside className="lg:sticky lg:top-28 h-fit min-w-0">
          <div className="border border-gray-200 rounded-2xl p-6 bg-white">
            <h1 className="font-display font-extrabold text-xl text-gray-900 leading-tight" data-testid="vehicle-detail-title">{vehicleTitle(v)}</h1>
            <div className="mt-1 text-xs text-gray-500 inline-flex items-center gap-1"><MapPin size={11} />{v.location}</div>
            <div className="mt-4 text-3xl font-display font-extrabold text-[#940C2C]" data-testid="vehicle-detail-price">{formatPriceLakh(v.price)}</div>
            <div className="mt-4 flex gap-2 flex-wrap">
              {v.cs_certified && <span className="badge-red text-[10px] font-bold px-2 py-1 rounded-md uppercase">CS Certified</span>}
              {v.owners === 1 && <span className="bg-gray-100 text-gray-800 text-[10px] font-bold px-2 py-1 rounded-md uppercase">1st Owner</span>}
              {isSold ? <span className="bg-[#777] text-white text-[10px] font-bold px-2 py-1 rounded-md uppercase">Sold</span>
                : <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-1 rounded-md uppercase">Available</span>}
            </div>

            {!isSold && (
              <div className="mt-5 space-y-2">
                <button onClick={() => setTdOpen(true)} className="btn-primary w-full" data-testid="detail-test-drive-btn"><Calendar size={16} /> Book Test Drive</button>
                <a href={buildWhatsAppLink(whatsapp, waMsg)} target="_blank" rel="noreferrer" className="btn-whatsapp w-full relative" data-testid="detail-whatsapp-btn">
                  <MessageCircle size={16} /> WhatsApp Us {whatsappIsDemo && <DemoTag />}
                </a>
                <a href={`tel:${phone}`} className="btn-dark w-full relative" data-testid="detail-call-btn">
                  <Phone size={16} /> Call Showroom {phoneIsDemo && <DemoTag />}
                </a>
                <button onClick={shareOnWA} className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-200 text-gray-800 font-semibold py-2.5 hover:border-gray-900 transition-colors" data-testid="detail-share-btn">
                  <Share2 size={16} /> Share on WhatsApp
                </button>
                <button onClick={() => toggle(v.id)} className="w-full inline-flex items-center justify-center gap-2 rounded-lg border-2 border-gray-200 text-gray-800 font-semibold py-2.5 hover:border-gray-900 transition-colors">
                  <Heart size={16} className={has(v.id) ? "fill-[#940C2C] text-[#940C2C]" : ""} /> {has(v.id) ? "Shortlisted" : "Add to Shortlist"}
                </button>
                {(phoneIsDemo || whatsappIsDemo) && (
                  <p className="text-[11px] text-gray-400 mt-2 leading-snug">
                    <strong>DEMO</strong> contact — admin to set real Carz Swag numbers in Settings before launch.
                  </p>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {similar.length > 0 && (
        <section className="cs-container pb-16">
          <h2 className="font-display font-extrabold text-2xl text-gray-900">Similar Cars</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {similar.map((s) => <VehicleCard key={s.id} vehicle={s} />)}
          </div>
        </section>
      )}

      {lightbox && <PhotoLightbox images={v.images.map(toAbs)} index={imgIdx} onIndex={setImgIdx} onClose={() => setLightbox(false)} title={vehicleTitle(v)} />}
      <TestDriveDialog vehicle={v} open={tdOpen} onOpenChange={setTdOpen} />
      <MobileStickyBar vehicle={v} onBookTestDrive={() => setTdOpen(true)} />
    </div>
  );
}

const centerThumb = (el) => {
  if (!el) return;
  const strip = el.parentElement;
  const delta = el.getBoundingClientRect().left - strip.getBoundingClientRect().left;
  strip.scrollTo({ left: strip.scrollLeft + delta - (strip.clientWidth - el.clientWidth) / 2, behavior: "smooth" });
};

const EMICalculator = ({ carPrice }) => {
  const [down, setDown] = useState(Math.round(carPrice * 0.2));
  const [rate, setRate] = useState(10);
  const [tenure, setTenure] = useState(60);
  const principal = Math.max(0, carPrice - down);
  const r = rate / 100 / 12;
  const n = tenure;
  const emi = r === 0 ? principal / n : (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n;
  const interest = total - principal;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-5" data-testid="emi-calculator">
      <div className="flex items-center gap-2 text-sm font-bold text-gray-900"><Calculator size={16} className="text-[#940C2C]" /> Estimate your monthly EMI</div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
        <SliderInput label="Down Payment" value={down} onChange={(v) => setDown(Math.min(carPrice, Math.max(0, v)))} min={0} max={carPrice} step={10000} display={formatPriceFull(down)} testid="emi-down" />
        <SliderInput label="Interest Rate (%)" value={rate} onChange={(v) => setRate(Math.min(20, Math.max(5, v)))} min={5} max={20} step={0.1} display={`${rate.toFixed(1)}%`} testid="emi-rate" />
        <SliderInput label="Tenure (months)" value={tenure} onChange={(v) => setTenure(Math.min(84, Math.max(12, v)))} min={12} max={84} step={6} display={`${tenure} mo`} testid="emi-tenure" />
      </div>
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <EmiBox label="Monthly EMI" value={formatPriceFull(Math.round(emi))} primary testid="emi-monthly" />
        <EmiBox label="Loan Amount" value={formatPriceFull(principal)} />
        <EmiBox label="Total Interest" value={formatPriceFull(Math.round(interest))} />
      </div>
      <p className="mt-3 text-[11px] text-gray-400">Estimate only — actual EMI depends on final loan terms from your financing partner.</p>
    </div>
  );
};

const SliderInput = ({ label, value, onChange, min, max, step, display, testid }) => (
  <div>
    <div className="flex items-center justify-between text-xs">
      <span className="font-semibold text-gray-600">{label}</span>
      <span className="font-bold text-gray-900">{display}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-2 w-full accent-[#940C2C]" data-testid={testid} />
  </div>
);

const EmiBox = ({ label, value, primary, testid }) => (
  <div className={`rounded-lg p-3 ${primary ? "bg-[#940C2C] text-white" : "bg-white border border-gray-200"}`} data-testid={testid}>
    <div className={`text-[10px] uppercase tracking-wider font-bold ${primary ? "text-white/80" : "text-gray-500"}`}>{label}</div>
    <div className={`mt-1 font-display font-extrabold text-lg ${primary ? "text-white" : "text-gray-900"}`}>{value}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="border-t border-gray-100 pt-6">
    <h3 className="font-display font-bold text-lg text-gray-900 mb-4">{title}</h3>
    {children}
  </div>
);
const Stat = ({ icon, label, value }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 p-3">
    <div className="text-[11px] uppercase tracking-wider text-gray-500 flex items-center gap-1">{icon}{label}</div>
    <div className="mt-0.5 font-semibold text-gray-900 text-sm break-words">{value}</div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2 border-b border-gray-50 py-1.5">
    <span className="text-gray-500">{label}</span>
    <span className="font-semibold text-gray-900 text-right break-words min-w-0">{value}</span>
  </div>
);
const DemoTag = () => <span className="ml-1 bg-black/25 text-[9px] font-bold uppercase tracking-wider rounded px-1 py-0.5">DEMO</span>;
